import { DateTime } from 'luxon';
import { ProcessedFlightHistory } from '../../types/flight';

type FlatStringObject = { [key: string]: string };

const timeLabels: FlatStringObject = {
  SCHEDULED: 'Scheduled',
  ESTIMATED: 'Estimated',
  ACTUAL: 'Actual',
};

const gateOrRunway: FlatStringObject = {
  GATE: 'Gate',
  RUNWAY: 'Runway',
};

const arrivalOrDeparture: FlatStringObject = {
  ARRIVAL: 'Arrival',
  DEPARTURE: 'Departure',
};

type GateOrRunway = typeof gateOrRunway[keyof typeof gateOrRunway];
type ArrivalOrDeparture = typeof arrivalOrDeparture[keyof typeof arrivalOrDeparture];

const defaultSecondTime = {
  label: timeLabels.ACTUAL,
  dateTime: null,
};

/**
 * Picks the second time for the flight data.
 * @param {ProcessedFlightHistory | null} flightData - The flight data.
 * @param {GateOrRunway} gateOrRunway - The gate or runway.
 * @param {ArrivalOrDeparture} arrivalOrDeparture - The arrival or departure.
 * @returns {LabeledDateTime} The labeled date-time.
 */
const pickSecondTime = (
  flightData: any,
  gateOrRunway: GateOrRunway,
  arrivalOrDeparture: ArrivalOrDeparture,
): LabeledDateTime => {
  if (flightData) {
    const potentialActual =
      flightData?.[
        `${timeLabels.ACTUAL.toLowerCase()}${gateOrRunway}${arrivalOrDeparture}`
      ];
    const potentialEstimated =
      flightData?.[
        `${timeLabels.ESTIMATED.toLowerCase()}${gateOrRunway}${arrivalOrDeparture}`
      ];

    let result;
    (potentialActual &&
      (result = {
        label: timeLabels.ACTUAL,
        dateTime: potentialActual,
      })) ||
      (potentialEstimated &&
        (result = {
          label: timeLabels.ESTIMATED,
          dateTime: potentialEstimated,
        })) ||
      (result = defaultSecondTime);
    return result;
  } else {
    return defaultSecondTime;
  }
};

export interface LabeledDateTime {
  label: string;
  dateTime: DateTime | null;
}

export interface TimeRowData {
  scheduled: LabeledDateTime;
  second: LabeledDateTime;
}

interface TimeColData {
  gate: TimeRowData;
  runway: TimeRowData;
}

interface StructuredDates {
  arrival: TimeColData;
  departure: TimeColData;
}

/**
 * Gets the structured dates for the flight data.
 * @param {ProcessedFlightHistory | null} flightData - The flight data.
 * @returns {StructuredDates} The structured dates.
 */
export const getStructuredDates = (
  flightData: any,
): StructuredDates => {
  const dateTimeData: any = {};
  Object.values(arrivalOrDeparture).forEach(ad => {
    const lowerAd = ad.toLowerCase();
    if (!dateTimeData[lowerAd]) {
      dateTimeData[lowerAd] = {
        gate: {},
        runway: {},
      };
    }
    Object.values(gateOrRunway).forEach(gr => {
      const lowerGr = gr.toLowerCase();
      dateTimeData[lowerAd][lowerGr].scheduled = {
        label: timeLabels.SCHEDULED,
        dateTime: flightData?.[`scheduled${gr}${ad}`] || null,
      };
      dateTimeData[lowerAd][lowerGr].second = pickSecondTime(
        flightData,
        gr,
        ad,
      );
    });
  });

  return dateTimeData as StructuredDates;
};